/**
 * PHSIMS Page Loading Skeletons
 * Author: Iswe Solutions Team
 * 
 * Provides ultra-thin loading states for lazy-loaded pages.
 * Matches the ZNPHI theme for a seamless loading experience.
 */

import React from 'react';
import { Box, Skeleton, Paper, keyframes } from '@mui/material';

// Subtle pulse animation for loading state
const pulseAnimation = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 0.8; }
  100% { opacity: 0.6; }
`;

/**
 * Card skeleton for dashboard cards and panels
 */
export const CardSkeleton: React.FC<{ height?: number }> = ({ height = 120 }) => (
  <Paper
    sx={{
      p: 2,
      height,
      animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
      bgcolor: 'background.paper',
      borderRadius: 2,
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Skeleton variant="text" width="60%" height={24} />
      <Skeleton variant="circular" width={24} height={24} />
    </Box>
    <Skeleton variant="text" width="80%" height={32} sx={{ mt: 1 }} />
    <Skeleton variant="text" width="40%" height={16} sx={{ mt: 1 }} />
  </Paper>
);

/**
 * Dashboard page skeleton with metric cards and chart area
 */
export const DashboardSkeleton: React.FC = () => (
  <Box sx={{ p: 3, bgcolor: '#f1f5f9', minHeight: '100vh' }}>
    {/* Header */}
    <Box sx={{ mb: 3 }}>
      <Skeleton variant="text" width={250} height={40} />
      <Skeleton variant="text" width={400} height={20} sx={{ mt: 1 }} />
    </Box>
    
    {/* Metric Cards Row */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
      {[1, 2, 3, 4].map((i) => (
        <CardSkeleton key={i} height={100} />
      ))}
    </Box>
    
    {/* Main Chart Area */}
    <Paper sx={{ p: 2, mb: 3, height: 300, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Skeleton variant="text" width={200} height={28} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={80} height={32} />
          <Skeleton variant="rounded" width={80} height={32} />
        </Box>
      </Box>
      <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: 1 }} />
    </Paper>
    
    {/* Table Area */}
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Skeleton variant="text" width={180} height={28} sx={{ mb: 2 }} />
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
      ))}
    </Paper>
  </Box>
);

/**
 * Table page skeleton for list views
 */
export const TableSkeleton: React.FC = () => (
  <Box sx={{ p: 3, bgcolor: '#f1f5f9', minHeight: '100vh' }}>
    {/* Header with actions */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
      <Skeleton variant="text" width={250} height={40} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton variant="rounded" width={120} height={36} />
        <Skeleton variant="rounded" width={100} height={36} />
      </Box>
    </Box>
    
    {/* Search/Filter bar */}
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Skeleton variant="rounded" width={300} height={40} />
        <Skeleton variant="rounded" width={150} height={40} />
        <Skeleton variant="rounded" width={150} height={40} />
      </Box>
    </Paper>
    
    {/* Table */}
    <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', gap: 2, p: 2, bgcolor: 'grey.100' }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="text" width={`${20 - i * 2}%`} height={24} />
        ))}
      </Box>
      {/* Data rows */}
      {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
        <Box key={row} sx={{ display: 'flex', gap: 2, p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {[1, 2, 3, 4, 5].map((col) => (
            <Skeleton key={col} variant="text" width={`${18 - col * 2}%`} height={20} />
          ))}
        </Box>
      ))}
    </Paper>
  </Box>
);

/**
 * Chat/AI page skeleton
 */
export const ChatSkeleton: React.FC = () => (
  <Box sx={{ p: 3, bgcolor: '#f1f5f9', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    {/* Header */}
    <Box sx={{ mb: 2 }}>
      <Skeleton variant="text" width={200} height={36} />
    </Box>
    
    {/* Chat messages area */}
    <Paper sx={{ flex: 1, p: 2, mb: 2, minHeight: 400, borderRadius: 2 }}>
      {[1, 2, 3].map((i) => (
        <Box key={i} sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Skeleton variant="circular" width={36} height={36} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" width={i % 2 ? '70%' : '50%'} height={60} />
          </Box>
        </Box>
      ))}
    </Paper>
    
    {/* Input area */}
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Skeleton variant="rounded" width="100%" height={56} />
    </Paper>
  </Box>
);

/**
 * Form page skeleton for configuration/settings pages
 */
export const FormSkeleton: React.FC = () => (
  <Box sx={{ p: 3, bgcolor: '#f1f5f9', minHeight: '100vh' }}>
    <Skeleton variant="text" width={300} height={40} sx={{ mb: 3 }} />
    
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      {[1, 2, 3, 4].map((i) => (
        <Box key={i} sx={{ mb: 3 }}>
          <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" width="100%" height={56} />
        </Box>
      ))}
      
      <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
        <Skeleton variant="rounded" width={100} height={42} />
        <Skeleton variant="rounded" width={100} height={42} />
      </Box>
    </Paper>
  </Box>
);

/**
 * Compact loading spinner for smaller components
 */
export const CompactLoader: React.FC<{ text?: string }> = ({ text = 'Loading...' }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      gap: 2,
      p: 4,
      color: 'primary.main',
    }}
  >
    <Box
      sx={{
        width: 24,
        height: 24,
        border: '3px solid',
        borderColor: 'primary.light',
        borderTopColor: 'primary.main',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        '@keyframes spin': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' },
        },
      }}
    />
    <Box sx={{ fontSize: 14, fontWeight: 500 }}>{text}</Box>
  </Box>
);

/**
 * Default page skeleton - used as Suspense fallback
 */
const PageSkeleton: React.FC = () => <DashboardSkeleton />;

export default PageSkeleton;
